import { useState, useEffect } from "react";
import { toast } from "sonner";

const FIELDS = [
  { key: "persona_name", label: "Persona name", type: "input" },
  { key: "agent_name", label: "Agent name (shown in the header)", type: "input" },
  { key: "tagline", label: "Tagline", type: "input" },
  { key: "header_tagline", label: "Header tagline", type: "input" },
  { key: "greeting", label: "Opening greeting (Variant A)", type: "textarea" },
  { key: "greeting_b", label: "Greeting Variant B (A/B test — clear this to turn it off)", type: "textarea" },
  { key: "system_prompt", label: "System prompt", type: "textarea", rows: 6 },
  { key: "lead_rules", label: "New enquiry rules", type: "textarea", rows: 4 },
  { key: "collaborator_rules", label: "Existing client rules", type: "textarea", rows: 4 },
  { key: "fallback_message", label: "When the agent doesn't know something", type: "textarea", rows: 3 },
  { key: "escalation_email", label: "Escalation email", type: "input" },
];

export const ConfigView = ({ config, api, onSaved, authHeaders }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        ...config,
        suggested_prompts_text: (config.suggested_prompts || []).join("\n"),
        companies_text: (config.companies || []).map((c) => `${c.name} :: ${c.description}`).join("\n"),
      });
    }
  }, [config]);

  if (!form) return <p className="text-base font-medium text-[#D6D6D6]">Loading...</p>;

  const save = async () => {
    setBusy(true);
    const payload = {};
    FIELDS.forEach((f) => { payload[f.key] = form[f.key]; });
    payload.suggested_prompts = form.suggested_prompts_text.split("\n").map((s) => s.trim()).filter(Boolean);
    payload.companies = form.companies_text.split("\n").filter(Boolean).map((line) => {
      const [name, ...rest] = line.split("::");
      return { name: name.trim(), description: rest.join("::").trim() };
    });
    payload.sms_followup_enabled = form.sms_followup_enabled;
    payload.llm_model = form.llm_model;
    try {
      const res = await fetch(`${api}/admin/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success("Saved — the agent is updated live.");
      onSaved();
    } catch {
      toast.error("The save didn't go through. Please try again.");
    }
    setBusy(false);
  };

  const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-[15px] font-medium text-white outline-none focus:border-[#FF5C5C] w-full";
  const label = "text-[15px] font-semibold text-[#D6D6D6] block mb-2";

  return (
    <div className="max-w-3xl space-y-5" data-testid="config-view">
      <div>
        <label className={label}>Agent model (Claude — switches live, no restart)</label>
        <select className={field} value={form.llm_model || "claude-sonnet-4-6"} onChange={(e) => setForm({ ...form, llm_model: e.target.value })} data-testid="config-llm_model">
          {(config?.available_models || [{ id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" }]).map((m) => (
            <option key={m.id} value={m.id} className="bg-black">{m.label}</option>
          ))}
        </select>
      </div>
      {FIELDS.map((f) => (
        <div key={f.key}>
          <label className={label}>{f.label}</label>
          {f.type === "input" ? (
            <input className={field} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`config-${f.key}`} />
          ) : (
            <textarea className={field} rows={f.rows || 3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`config-${f.key}`} />
          )}
        </div>
      ))}
      <div>
        <label className={label}>Companies (one per line: Name :: Description)</label>
        <textarea className={field} rows={4} value={form.companies_text} onChange={(e) => setForm({ ...form, companies_text: e.target.value })} data-testid="config-companies" />
      </div>
      <div>
        <label className={label}>Suggested prompts (one per line)</label>
        <textarea className={field} rows={4} value={form.suggested_prompts_text} onChange={(e) => setForm({ ...form, suggested_prompts_text: e.target.value })} data-testid="config-prompts" />
      </div>
      <label className="flex items-center gap-3 text-[15px] font-medium text-[#D6D6D6] cursor-pointer">
        <input type="checkbox" checked={!!form.sms_followup_enabled} onChange={(e) => setForm({ ...form, sms_followup_enabled: e.target.checked })} data-testid="config-sms-toggle" className="accent-white w-4 h-4" />
        SMS follow-up enabled (Twilio — currently mocked)
      </label>
      <button onClick={save} disabled={busy} data-testid="config-save-button"
        className="rounded-xl bg-white text-black font-semibold text-base px-8 py-3 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
        {busy ? "Saving..." : "Save changes"}
      </button>
    </div>
  );
};
